/* eslint-disable */
import axios from 'axios';
import config from '@/env';
import { uploadHeaders } from '@/utils/crypto';
import { tencentLivenessRecognition } from '@/api/tool'

import Config from './config.js';
import {
  livenessEnum,
  livenessAssets,
  silentLivenessAsset,
  livenessLimit,
  hackErrorMap,
  livenessErrorMap,
  HACK_SCORE_MAX,
} from '../constants.js';
import { createObjectURL, revokeObjectURL } from '../compatible.js';
import { isSupported } from '../support.js';
import '../polyfill.js';

const { alert } = window;

// 活体检测后返回给客户的request_id的键
const LIVENESS_REQUEST_ID_KEY = 'liveness_request_id';
// 防hack后返回给客户的request_id的键
const HACK_REQUEST_ID_KEY = 'hack_request_id';
const REQUEST_ID_KEY = 'request_id';

/**
 * 该方法用于将对象转换为使用[]表示的字符串形式。
 * 比如，对象为：
 * {
 *  a:'a',
 *  b:[{
 *    c:'c'
 *  }, 1]
 * }
 *
 * 如果在objectToQueryString方法的add中不使用encodeURIComponent，则最终的结果为：
 * 'a=a&b[0][c]=c&b[]=1'
 */
function buildParams(prefix, obj, add) {
  let name;
  let i;
  let l;
  let rbracket;
  rbracket = /\[\]$/;
  if (obj instanceof Array) {
    for (i = 0, l = obj.length; i < l; i++) {
      if (rbracket.test(prefix)) {
        add(prefix, obj[i]);
      } else {
        buildParams(`${prefix}[${typeof obj[i] === 'object' ? i : ''}]`, obj[i], add);
      }
    }
  } else if (typeof obj === 'object') {
    for (name in obj) { // Serialize object item.
      buildParams(`${prefix}[${name}]`, obj[name], add);
    }
  } else {
    add(prefix, obj); // Serialize scalar item.
  }
}

const objectToQueryString = function (a) {
  let prefix;
  let s;
  let add;
  let name;
  let r20;
  let output;
  s = [];
  // %20指的是空格，在encodeURIComponent方法中会将空格' '转换为'%20'
  r20 = /%20/g;
  add = function (key, value) {
    value = (typeof value === 'function') ? value() : (value == null ? '' : value);
    s[s.length] = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  };
  if (a instanceof Array) {
    for (name in a) {
      add(name, a[name]);
    }
  } else {
    for (prefix in a) {
      buildParams(prefix, a[prefix], add);
    }
  }

  // 需要将'%20'转换为'+'的原因是：使用encodeURIComponent方法时，会将空格' '转换为'%20'，但是在form表单中会将' '编码为'+'，
  // 比如存在一个input的name为'hello world'，则会将其编码为'hello+world'，所以这里是在模拟form表单的编码。
  output = s.join('&').replace(r20, '+');
  return output;
};

function omit(obj, keys) {
  if (typeof obj === 'undefined' || obj === null
    || !(typeof obj === 'object' || typeof obj === 'function')) {
    return result;
  }

  keys = [].concat.apply([], [].slice.call(arguments, 1));
  const last = keys[keys.length - 1];
  const res = {};
  let fn;

  if (typeof last === 'function') {
    fn = keys.pop();
  }

  const isFunction = typeof fn === 'function';
  if (!keys.length && !isFunction) {
    return obj;
  }

  const hasOwn = Object.prototype.hasOwnProperty;
  for (const key in obj) {
    if (hasOwn.call(obj, key) && keys.indexOf(key) === -1) {
      if (!isFunction) {
        res[key] = obj[key];
      } else if (fn(obj[key], key, obj)) {
        res[key] = obj[key];
      }
    }
  }
  return res;
}

const ajax = function (options = {}) {
  if (!options.url) { return; }

  const xhr = new XMLHttpRequest();
  xhr.open('POST', options.url, true);
  xhr.onreadystatechange = () => {
    if (xhr.readyState === 4) {
      if (xhr.status === 200) {
        options.success && options.success(JSON.parse(xhr.responseText));
      } else {
        let error;
        try {
          error = JSON.parse(xhr.responseText);
        } catch (e) {
          error = null;
        } finally {
          options.error && options.error(error);
        }
      }
    }
  };
  xhr.onerror = (err) => {
    options.error && options.error(err);
  };

  if (typeof options.data === 'object' && options.data.constructor === Object) {
    // 设置为默认的表单提交方式，这里需要特别指定的原因是在XMLHttpRequest 2的规范中，默认的Content-type头
    // 的值为'multipart/form-data'。
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    // 注意，只有在options.data为对象的情况下才会调用该方法。
    xhr.send(objectToQueryString(options.data));
  } else {
    xhr.send(options.data);
  }
  return xhr;
};

// 系统对于上传的文件有一些限制，限制在constants.js --> livenessLimit属性中定义。
// 该方法用于在前端对待上传的视频文件是否符合限制进行检测。
const check = function (file) {
  // 为什么要用Promise处理？
  // 因为在对视频进行检测时存在一些异步操作。
  return new Promise((resolve, reject) => {
    // 视频文件大小超过限制则报错
    if (file.size > livenessLimit.size) {
      reject(Error(`视频大小需限制在${livenessLimit.size / 1024 / 1024}M以内`));
      return;
    }

    const video = document.createElement('video');
    let metadataloaded = false;
    // preload用于指定video预加载的信息，metadata表示预加载视频的元数据，也就是视频的大小、持续时间等信息。
    video.preload = 'metadata';
    // onloadedmetadata事件的回调时机：当用户代理（也就是浏览器）刚决定媒体资源的持续时间和大小时被调用。
    video.onloadedmetadata = () => {
      metadataloaded = true;
      // 释放URL对象。
      revokeObjectURL(video.src);

      // 视频持续时间是否超过限制。
      if (video.duration > livenessLimit.duration) {
        reject(Error(`视频时长需限制在${livenessLimit.duration}秒以内`));
      } else {
        resolve({ file });
      }
    };
    video.src = createObjectURL(file);

    // Todo 什么情况下metadataloaded会在2秒钟以后仍为false，是因为视频解析太慢？因为解析出错？
    setTimeout(() => {
      if (!metadataloaded) {
        resolve({ file });
      }
    }, 2000);
  });
};

/**
 * 属性：
 * options：构造函数时传入的选项对象。
 * config：配置对象。
 * action：保存需要用户做的动作，也就是用户需要面对摄像头做的动作。具体动作信息存储在constants.js --> livenessAssets变量中
 * mask：指向dom对象，该dom对象是弹出框后面半透明的遮挡层。
 * modal：指向dom对象，该dom对象是具体显示内容的弹出框。
 * loadModal：录制或选中的视频文件开始上传后显示提示信息的dom对象。
 * uploadInput：type为file的input元素，在model弹出框中存在，用于上传用户录制或选中的文件。
 */
export default class LivenessSDK {
  constructor(options) {
    // 选项对象
    this.options = options || {};
    if (!this.options.onChecked) {
      throw new Error('Please provide callback for check');
    }

    if (!this.options.silent && !this.options.action) {
      throw new Error('Please configure silent liveness or  provide specific liveness action among nod, open mouth and yaw');
    }

    // 配置对象
    this.config = new Config(options);
  }

  // 是否支持当前浏览器
  static isAvailable = isSupported()

  // 获得需要用户做的动作，也就是用户需要面对摄像头做的动作
  getAction() {
    if (this.options.silent) { // 静默活体检测
      this.action = [silentLivenessAsset];
    } else { // 活体检测
      this.action = this.options.action.split(',').map(i => livenessAssets[i]);
    }
  }

  // 创建并返回实际显示内容的弹出框的dom对象
  getModal() {
    const mask = this.getMask();

    // modal指向dom对象，表示真正显示内容的弹出框。
    if (!this.modal) {
      this.modal = document.createElement('div');
      this.modal.className = 'modal liveness-modal';
      this.modal.innerHTML = `<div class="modal-body">
                                <div class="sample">
                                  <div class="sample-inner">
                                    <div class="gif-container">${this.action.map(i => `<img class="git" src="${i.gif}"/>`).join('')}</div>
                                  </div>
                                </div>
                                <p>
                                  录制不超过<em> ${livenessLimit.duration}s </em>的视频<br>
                                  ${this.options.silent ? '<span class="justify">请保持<em>脸在屏幕内</em><span>' : `拍摄视频时请${this.action.map((item, i) => `${i === 0 ? '先':'再'}<em>${item.description}</em>`).join('')}`}
                                </p>
                              </div>`;
      mask.appendChild(this.modal);

      // 使用localStorage的API，保存名为viewed的信息。
      // viewed的作用是：如果当前用户是第一次访问当前系统，则会存在3秒的倒计时，倒计时完成之后才可点击"开始录制"的按钮，
      // 之后用户再次访问系统时，"开始录制"的按钮可直接点击，而不存在倒计时，而viewed就是用于表示用户是否是第一次访问系统（更确切的说，
      // 是在某一浏览器中），如果viewed为true，则说明已经在当前浏览器中访问过当前系统了。
      const haveViewed = !!localStorage.getItem('viewed');
      let countdown = 3;
      // button
      const button = document.createElement('button');
      button.id = 'confirm';

      // 如果用户之前在当前浏览器中访问过当前系统，则直接可以点击"开始录制"按钮
      // Todo 为什么要在第一次访问时倒计时3秒？
      if (haveViewed) {
        button.textContent = '开始录制';
        button.classList.add('enabled');
      } else {
        button.textContent = `开始录制 (${countdown})`;

        // 进行3秒的倒计时
        const interval = setInterval(() => {
          countdown--;
          if (countdown > 0) {
            button.textContent = `开始录制 (${countdown})`;
          } else {
            button.textContent = '开始录制';
            button.classList.add('enabled');
            if (!localStorage.getItem('viewed')) {
              localStorage.setItem('viewed', true);
            }
            clearInterval(interval);
          }
        }, 1000);
      }
      button.addEventListener('click', () => {
        if (countdown === 0 || haveViewed) {
          // uploadInput是弹出框中的视频上传的input dom对象。
          // 调用click方法是为了模仿用户对上传控件按钮的点击，从而
          // 可以调用相应的媒体设备或者弹出选择文件的对话框
          this.uploadInput.click();
        }
      });

      const body = this.modal.querySelector('.modal-body');
      body.appendChild(button);
    }

    // upload input
    this.clearUploadInput();

    return this.modal;
  }

  // Todo 该方法存在问题，在方法getModal中被调用，在方法unloading中也被调用，存在重复调用。在unloading方法中已经清理了一次，但是在再次显示弹出框时，又会清理一次。
  clearUploadInput() {
    if (!this.modal) {
      return;
    }

    const body = this.modal.querySelector('.modal-body');

    // 如果input Dom元素已存在，则先移除
    if (this.uploadInput) {
      body.removeChild(this.uploadInput);
      this.uploadInput = null;
    }

    this.uploadInput = document.createElement('input');
    this.uploadInput.className = 'upload-input';
    this.uploadInput.setAttribute('type', 'file');
    // accept属性规定能够通过文件上传进行提交的文件类型，'video/*'MIME类型，说明接受任意视频类型。
    this.uploadInput.setAttribute('accept', 'video/*');
    // capture表示，可以捕获到系统默认的设备，capture需要同accept配合使用，accept用于指定capture捕获的
    // 设备的类型，因为capture属性的值的类型为boolean类型，accept的值与设备类型的对应关系：
    // accept="image/*" => 相册/照相机
    // accept="video/*" => 摄像机
    // accept="audio/*" => 录音
    // 当然，如果在pc端的浏览器中使用，会直接跳出选择文件的对话框。
    this.uploadInput.setAttribute('capture', 'user');
    this.uploadInput.addEventListener('change', this.handleUpload.bind(this));
    body.appendChild(this.uploadInput);
  }

  // 该方法用于创建并返回弹出框后面半透明的遮挡层的dom对象。
  getMask() {
    // mask指向的dom对象是弹出框后面半透明的遮挡层。
    if (!this.mask) { // 只需创建一次
      this.mask = document.createElement('div');
      this.mask.className = 'liveness-modal-mask';
      // click事件主要做的工作是做一些清理工作，并隐藏弹出框
      this.mask.addEventListener('click', (e) => {
        e.stopPropagation();
        if (e.target === this.mask) {
          // Todo xhr是干什么的？
          if (this.xhr) {
            this.xhr.abort();
          }

          // Todo hackXhr是干什么的？
          if (this.hackXhr) {
            this.hackXhr.abort();
          }
          this.mask.classList.remove('opened');
        }
      });
      document.body.appendChild(this.mask);
    }
    return this.mask;
  }

  // 当媒体设备工作完成或者在文件选择对话框中选中文件时，会触发uploadInput的change事件，从而
  // 调用当前函数
  handleUpload(event) {
    // 选中的文件，类型为File
    const file = event.target.files[0];
    const beforeCheck = this.options.beforeCheck || function () {
    };
    beforeCheck.call(this, { video_file: file });
    if (file) {
      this.loading();
      check(file).then(() => {
        const { onChecked } = this.options;
        onChecked(file)
      }).catch(err => {
        this.handleResult(err);
      })
    }
  }

  // 处理上传完成后的回传数据
  handleResult(err, payload = {}) {
    this.genErrorResponse(err.message);
    this.unloading();
  }

  handleHack(livenessData) {
    const { onChecked } = this.options;
    const responseData = Object.assign({}, livenessData);
    const requestIdData = {
      [LIVENESS_REQUEST_ID_KEY]: livenessData && livenessData[LIVENESS_REQUEST_ID_KEY],
    };

    // hack check
    this.hackXhr = ajax({
      url: this.config.livenessHackApi(),
      data: {
        image_id: livenessData.feature_image_id,
      },
      success: (hackData) => {
        if (hackData.status === 'OK') {
          responseData.hackPassed = (hackData.score <= HACK_SCORE_MAX);
          responseData.hackScore = hackData.score;
          responseData[HACK_REQUEST_ID_KEY] = hackData.request_id;
          onChecked(responseData);
          // this.hide(); // 改为 所有情况都会 在 unloading 中 先移除这个弹层
        }
        this.unloading();
      },
      error: (err) => {
        const hackRequestIdData = {
          ...requestIdData,
          [HACK_REQUEST_ID_KEY]: err && err[REQUEST_ID_KEY],
        };

        if (hackErrorMap[err.status]) {
          this.genErrorResponse(hackErrorMap[err.status], hackRequestIdData);
        } else {
          this.genErrorResponse('请求服务器出错，请重试！', hackRequestIdData);
        }
        this.unloading();
      },
    }, (err) => {
      const hackRequestIdData = {
        ...requestIdData,
        [HACK_REQUEST_ID_KEY]: err && err[REQUEST_ID_KEY],
      };

      this.genErrorResponse('请求服务器出错，请重试！', hackRequestIdData);
      this.unloading();
    });
  }

  // 该方法用于生成错误响应给到客户
  genErrorResponse(message, data) {
    const onError = this.options.onError || function (data) {
      alert(data.message);
    };

    data = data || {};

    onError({
      message,
      ...data,
    });
  }

  // 创建并返回录制或选中的视频文件开始上传后显示提示信息的dom对象
  getLoader() {
    if (!this.loadModal) {
      const mask = this.getMask();
      const loadModal = document.createElement('div');
      loadModal.className = 'modal loading-modal';
      loadModal.innerHTML = `<div class="modal-body">
                               <div class="gif-container"><img src="/images/cloud.gif" alt="Loading"></div>
                               <p>上传中, 预计时间1-3分钟...</p>
                               <div class="pop-modal"></div>
                             </div>`;
      mask.appendChild(loadModal);
      this.loadModal = loadModal;
    }
    return this.loadModal;
  }

  // 显示正在加载的提示信息，并隐藏弹出框
  loading() {
    const loadModal = this.getLoader();
    // show loading
    if (this.modal) {
      this.modal.classList.add('hide');
    }
    loadModal.classList.remove('hide');
  }

  /**
   * 隐藏loading状态，显示上传文件的对话框
   * @param holdMask 如果为true，则会不隐藏整个弹出框。
   */
  unloading(holdMask) {
    const modal = this.getModal();
    const loadModal = this.getLoader();

    loadModal.classList.add('hide');
    this.clearUploadInput();
    modal.classList.remove('hide');
    if (!holdMask) {
      this.hide();
    }
  }

  show() {
    this.getAction();
    this.getModal();

    const mask = this.getMask();
    mask.classList.add('opened');
    return mask;
  }

  hide() {
    if (this.mask) {
      this.mask.classList.remove('opened');
    }
  }
}
